export interface TransportProviderTeamDetail {
  id: string;
  name: string;
  color?: string;
}

/** Resolves team rows for labels from API payloads (teams_detail and/or parsed `teams`). */
export function teamsForTransportProviderLabel(row: {
  teams_detail?: TransportProviderTeamDetail[] | null;
  teams?: unknown;
}): TransportProviderTeamDetail[] | undefined {
  if (row.teams_detail?.length) {
    return row.teams_detail;
  }
  const teams = row.teams;
  if (!Array.isArray(teams) || teams.length === 0) {
    return undefined;
  }
  const first = teams[0];
  if (
    typeof first === 'object' &&
    first !== null &&
    'name' in first &&
    typeof (first as { name?: unknown }).name === 'string'
  ) {
    return teams as TransportProviderTeamDetail[];
  }
  return undefined;
}

/** Lowercase trimmed key for grouping duplicate provider display names (case-insensitive). */
export function providerDisplayNameKey(
  name: string | null | undefined
): string {
  return (name ?? '').trim().toLowerCase();
}

/** Counts how many selectable rows share each normalized display name. */
export function countRowsByProviderDisplayName<T>(
  rows: T[],
  getName: (row: T) => string | null | undefined
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const k = providerDisplayNameKey(getName(row));
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

/** True when more than one row uses this display name — show team suffix on labels. */
export function shouldShowProviderTeamLabel(
  name: string | null | undefined,
  countsByKey: Map<string, number>
): boolean {
  return (countsByKey.get(providerDisplayNameKey(name)) ?? 0) > 1;
}

export interface FormatTransportProviderOptionLabelOptions {
  /** When false, omit team names even if `teamsDetail` is present (single row for this name). */
  showTeamDisambiguation?: boolean;
}

/**
 * Builds a select label for a company transport / sensor row, appending team names
 * when disambiguation is needed (same display name used by multiple rows).
 */
export function formatTransportProviderOptionLabel(
  courierName: string | null | undefined,
  teamsDetail: TransportProviderTeamDetail[] | null | undefined,
  options?: FormatTransportProviderOptionLabelOptions
): string {
  const base = (courierName ?? '').trim() || '—';
  if (options?.showTeamDisambiguation === false) {
    return base;
  }
  if (!teamsDetail?.length) {
    return base;
  }
  const names = [...teamsDetail]
    .map((t) => (t?.name ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  if (names.length === 0) {
    return base;
  }
  return `${base} (${names.join(', ')})`;
}
